/**
 * ModulePageWrapper - Standard page wrapper for alle module pages
 * Kombinerer header, control panel, tabs og innholdsområde
 * @version 2.0.0 - MASTERPROMPT v2.0 compliant
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ErrorBoundary from '../../../components/ErrorBoundary';
import { RelatedModules } from '../../../components/InternalLinks';
import { usePageSEO } from '../../../components/SEO';
import { ModulePageHeader } from './ModulePageHeader';
import { ModuleControlPanel } from './ModuleControlPanel';
import { ModulePageTabs } from './ModulePageTabs';
import { getActiveDeadlinesCount } from '../utils/deadlineUtils';
import { UploadedDocumentsList } from '../UploadedDocumentsList';
import { CaseFolderManager } from '../CaseFolderManager';
import { ModuleStats } from '../dashboard/ModuleStats';

export function ModulePageWrapper({
  // Module config
  moduleId,
  title,
  badge = 'Master',
  badgeIcon = 'sparkles',
  lawReference,
  
  // Navigation
  tabs,
  defaultTab = 'oversikt',
  tabToGuideMap = {},
  guideRoute,
  backRoute = '/chat',
  
  // State (controlled)
  activeTab: controlledActiveTab,
  onTabChange: controlledOnTabChange,
  
  // Feature flags
  showChat = true,
  showDocuments = true,
  showGuide = true,
  showFrister = true,
  showRelatedModules = true,
  
  // Content
  children
}) {
  const [searchParams] = useSearchParams();
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab);
  const [activeDeadlines, setActiveDeadlines] = useState(0);
  
  // Support both controlled and uncontrolled mode
  const activeTab = controlledActiveTab ?? internalActiveTab;
  const setActiveTab = controlledOnTabChange ?? setInternalActiveTab;
  
  // Apply SEO meta tags
  usePageSEO(moduleId);
  
  // Handle tab from URL parameter (for deeplinks)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabs.some(t => t.id === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams, tabs, setActiveTab]);
  
  // Update deadline count when tab changes
  useEffect(() => {
    setActiveDeadlines(getActiveDeadlinesCount(moduleId));
  }, [activeTab, moduleId]);
  
  const isFristerActive = activeTab === 'frister';
  
  // Filter out frister from main tabs if it's handled separately
  const mainTabs = showFrister ? tabs.filter(t => t.id !== 'frister') : tabs;
  
  return (
    <div className="min-h-screen bg-[var(--bg-deep)]" data-testid={`${moduleId}-page`}>
      <div className="max-w-6xl mx-auto px-4 py-6">
        
        <ModulePageHeader
          title={title}
          badge={badge}
          badgeIcon={badgeIcon}
          lawReference={lawReference}
          backRoute={backRoute}
        />
        
        <ModuleControlPanel
          moduleId={moduleId}
          guideRoute={guideRoute}
          activeDeadlines={activeDeadlines}
          isFristerActive={isFristerActive}
          onFristerClick={showFrister ? () => setActiveTab('frister') : undefined}
          showChat={showChat}
          showDocuments={showDocuments}
          showGuide={showGuide}
          showFrister={showFrister}
        />
        
        <ModulePageTabs
          tabs={mainTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          moduleId={moduleId}
          guideBasePath={guideRoute}
          tabToGuideMap={tabToGuideMap}
        />
        
        <ErrorBoundary>
          <div className="animate-fadeIn">
            {typeof children === 'function' 
              ? children({ activeTab, setActiveTab, activeDeadlines })
              : children
            }
          </div>
        </ErrorBoundary>
        
        {/* Opplastede dokumenter & Saksmapper - vises i oversikt-fanen */}
        {activeTab === 'oversikt' && (
          <div className="mt-8 space-y-8">
            <ModuleStats moduleId={moduleId} />
            <CaseFolderManager moduleId={moduleId} />
            <UploadedDocumentsList moduleId={moduleId} />
          </div>
        )}
        
        {showRelatedModules && (
          <RelatedModules currentModule={moduleId} maxItems={3} />
        )}
      </div>
    </div>
  );
}

export default ModulePageWrapper;
